import { getApiBaseUrl } from '../config/env';
import { PLATFORM_NAME } from '../constants/brand';

const CACHE_KEY = 'jobscribe_site_config_v1';
const CACHE_TTL_MS = 60 * 1000; // 1 minute — respect admin toggles quickly

/** @type {Promise<object> | null} */
let inflightRequest = null;

const DEFAULT_CONFIG = {
  platform_name: PLATFORM_NAME,
  tagline: null,
  maintenance_mode: false,
  allow_company_registration: true,
  signup_allowed: true,
  signup_block_message: null,
};

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt || !parsed.config) return null;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return parsed.config;
  } catch {
    return null;
  }
}

function writeCache(config) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ fetchedAt: Date.now(), config })
    );
  } catch {
    // ignore
  }
}

export function getCachedSiteConfig() {
  return readCache();
}

/**
 * @param {{ forceRefresh?: boolean }} [options]
 */
export async function fetchSiteConfig(options = {}) {
  const { forceRefresh = false } = options;

  if (!forceRefresh) {
    const cached = readCache();
    if (cached) return cached;
  }

  if (inflightRequest) {
    return inflightRequest;
  }

  inflightRequest = (async () => {
    try {
      const base = getApiBaseUrl();
      const response = await fetch(`${base}/public/site-config`, {
        headers: { Accept: 'application/json' },
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || json?.error || 'Failed to load site configuration');
      }
      const config = json?.data ?? json;
      const normalized = {
        ...DEFAULT_CONFIG,
        ...config,
        signup_allowed: Boolean(config?.signup_allowed),
        allow_company_registration: Boolean(
          config?.allow_company_registration ?? DEFAULT_CONFIG.allow_company_registration
        ),
        maintenance_mode: Boolean(config?.maintenance_mode),
      };
      writeCache(normalized);
      return normalized;
    } catch (err) {
      console.warn('Site config fetch failed, using defaults:', err?.message || err);
      return { ...DEFAULT_CONFIG };
    } finally {
      inflightRequest = null;
    }
  })();

  return inflightRequest;
}

export function prefetchSiteConfig() {
  void fetchSiteConfig();
}
